import React, {useEffect, useState} from 'react';
import {API, showError, showInfo, showSuccess} from '../../helpers';
import {renderQuota} from '../../helpers/render';
import {Layout, Card, Button, Form, Divider, Space, Modal, Tabs, Tag, Spin} from "@douyinfe/semi-ui";
import Title from "@douyinfe/semi-ui/lib/es/typography/title";
import {IconCreditCard, IconHistory, IconGift, IconMoneyExchangeStroked, IconCoinMoney} from '@douyinfe/semi-icons';

const TopUp = () => {
    const [redemptionCode, setRedemptionCode] = useState('');
    const [topUpLink, setTopUpLink] = useState('');
    const [userQuota, setUserQuota] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [user, setUser] = useState({});
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('wechat');
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [showQRCode, setShowQRCode] = useState(false);
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [currentOrderNo, setCurrentOrderNo] = useState('');
    const [activeTab, setActiveTab] = useState('1');
    const [topUpPackages, setTopUpPackages] = useState([]);

    // 自定义微信图标组件
    const WechatIcon = () => (
        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
            <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.027-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z"/>
        </svg>
    );

    // 加载充值套餐
    const loadTopUpPackages = async () => {
        try {
            const res = await API.get('/api/package/');
            const { success, data } = res.data;
            if (success && data) {
                setTopUpPackages(data);
            }
        } catch (err) {
            console.error('加载充值套餐失败:', err);
            // 使用默认套餐作为降级方案
            setTopUpPackages([
                { id: 1, name: '体验版', quota: 1000000, price: 1.9, discount: null },
                { id: 2, name: '标准版', quota: 5000000, price: 39.9, discount: '8折' },
                { id: 3, name: '专业版', quota: 10000000, price: 69.9, discount: '7折' },
                { id: 4, name: '企业版', quota: 50000000, price: 299.9, discount: '6折' },
            ]);
        }
    };

    // 支付方式
    const paymentMethods = [
        { id: 'wechat', name: '微信支付', icon: <WechatIcon />, color: '#07C160', enabled: true },
        { id: 'alipay', name: '支付宝', icon: <IconCoinMoney style={{ fontSize: 24 }} />, color: '#1677FF', enabled: false }, // 默认禁用，需要后端配置
        // { id: 'stripe', name: 'Stripe', icon: <IconCreditCard style={{ fontSize: 24 }} />, color: '#635BFF' },
    ];
    const [availablePaymentMethods, setAvailablePaymentMethods] = useState(paymentMethods);

    // 加载支付历史
    const loadPaymentHistory = async () => {
        try {
            const res = await API.get('/api/payment/history', {
                params: { page: 1, per_page: 50 }
            });

            const { success, data } = res.data;
            if (success && data.payments) {
                const formattedHistory = data.payments.map(payment => ({
                    id: payment.id.toString(),
                    orderNo: payment.order_no,
                    amount: payment.amount,
                    quota: payment.quota,
                    method: payment.payment_method === 'wechat' ? '微信支付' : payment.payment_method === 'alipay' ? '支付宝' : 'Stripe',
                    status: payment.status === 1 ? '成功' : payment.status === 0 ? '待支付' : payment.status === 2 ? '已取消' : '已过期',
                    time: new Date(payment.created_at).toLocaleString('zh-CN')
                }));
                setPaymentHistory(formattedHistory);
            }
        } catch (err) {
            console.error('加载支付历史失败:', err);
        }
    };

    // 检查可用支付方式
    const checkAvailablePaymentMethods = async () => {
        try {
            // 从后端获取实际可用的支付方式
            const res = await API.get('/api/payment/methods');
            const { success, data } = res.data;
            
            if (success && data) {
                const methods = [
                    { id: 'wechat', name: '微信支付', icon: <WechatIcon />, color: '#07C160', enabled: data.wechat || false },
                    { id: 'alipay', name: '支付宝', icon: <IconCoinMoney style={{ fontSize: 24 }} />, color: '#1677FF', enabled: data.alipay || false },
                ];
                
                setAvailablePaymentMethods(methods);
                
                // 如果当前选中的支付方式不可用，切换到可用的支付方式
                const selectedMethod = methods.find(m => m.id === paymentMethod);
                if (!selectedMethod || !selectedMethod.enabled) {
                    const availableMethod = methods.find(m => m.enabled);
                    if (availableMethod) {
                        setPaymentMethod(availableMethod.id);
                    }
                }
            }
        } catch (err) {
            console.error('检查支付方式失败:', err);
            // 如果获取失败，默认只显示微信支付
            setAvailablePaymentMethods(paymentMethods);
        }
    };

    const topUp = async () => {
        if (redemptionCode === '') {
            showInfo('请输入兑换码！')
            return;
        }
        setIsSubmitting(true);
        try {
            const res = await API.post('/api/user/topup', {
                key: redemptionCode
            });
            const {success, message, data} = res.data;
            if (success) {
                showSuccess('兑换成功！');
                Modal.success({title: '兑换成功！', content: '成功兑换额度：' + renderQuota(data), centered: true});
                setUserQuota((quota) => {
                    return quota + data;
                });
                setRedemptionCode('');
            } else {
                showError(message);
            }
        } catch (err) {
            showError('请求失败');
        } finally {
            setIsSubmitting(false);
        }
    };

    const openTopUpLink = () => {
        if (!topUpLink) {
            showError('超级管理员未设置充值链接！');
            return;
        }
        let url = new URL(topUpLink);
        let username = user.username;
        let user_id = user.id;
        url.searchParams.append('username', username);
        url.searchParams.append('user_id', user_id);
        url.searchParams.append('transaction_id', crypto.randomUUID());
        window.open(url.toString(), '_blank');
    };

    // 发起支付
    const handlePayment = async () => {
        if (!selectedPackage) {
            showError('请选择充值套餐');
            return;
        }

        // 检查支付方式是否可用
        const selectedMethod = availablePaymentMethods.find(m => m.id === paymentMethod);
        if (!selectedMethod || !selectedMethod.enabled) {
            showError('该支付方式暂未配置，请选择其他支付方式');
            return;
        }

        setIsLoading(true);
        try {
            // 调用后端支付 API
            const res = await API.post('/api/payment/create', {
                package_id: selectedPackage.id,
                payment_method: paymentMethod,
                amount: selectedPackage.price
            });

            const { success, message, data } = res.data;

            if (success) {
                showSuccess('支付二维码已生成,请扫码支付');
                setQrCodeUrl(data.qr_code_url);
                setCurrentOrderNo(data.order_no);
                setShowQRCode(true);

                // 开始轮询支付状态
                startPaymentPolling(data.order_no);
            } else {
                showError(message || '创建订单失败');
            }
        } catch (err) {
            showError('支付请求失败,请重试');
        } finally {
            setIsLoading(false);
        }
    };

    // 轮询支付状态
    let paymentPollingTimer = null;
    const startPaymentPolling = (orderNo) => {
        // 清除之前的轮询
        if (paymentPollingTimer) {
            clearInterval(paymentPollingTimer);
        }

        // 每3秒检查一次
        paymentPollingTimer = setInterval(async () => {
            try {
                const res = await API.get(`/api/payment/status/${orderNo}`);
                const { success, data } = res.data;

                if (success && data.status === 1) {
                    // 支付成功
                    clearInterval(paymentPollingTimer);
                    setShowQRCode(false);
                    showSuccess('支付成功!额度已添加到账户');

                    // 刷新用户额度
                    getUserQuota();

                    // 刷新支付记录
                    if (showHistory) {
                        loadPaymentHistory();
                    }
                } else if (success && (data.status === 2 || data.status === 3)) {
                    // 订单已取消或过期
                    clearInterval(paymentPollingTimer);
                    showError('订单已取消或过期');
                }
            } catch (err) {
                console.error('检查支付状态失败:', err);
            }
        }, 3000);
    };

    // 检查支付状态
    const checkPaymentStatus = async () => {
        if (!currentOrderNo) {
            showError('当前没有进行中的订单');
            return;
        }

        try {
            showInfo('正在检查支付状态...');

            const res = await API.get(`/api/payment/status/${currentOrderNo}`);
            const { success, data } = res.data;

            if (success) {
                if (data.status === 1) {
                    showSuccess('支付成功!额度已添加到账户');
                    setShowQRCode(false);
                    getUserQuota();
                    if (showHistory) {
                        loadPaymentHistory();
                    }
                } else if (data.status === 0) {
                    showInfo('订单待支付,请扫码完成支付');
                } else if (data.status === 2) {
                    showError('订单已取消');
                } else if (data.status === 3) {
                    showError('订单已过期');
                }
            }
        } catch (err) {
            showError('检查失败');
        }
    };

    // 复制订单号
    const copyOrderNo = async (orderNo) => {
        try {
            await navigator.clipboard.writeText(orderNo);
            showSuccess('订单号已复制');
        } catch (err) {
            showError('复制失败');
        }
    };

    const getUserQuota = async () => {
        let res = await API.get(`/api/user/self`);
        const {success, message, data} = res.data;
        if (success) {
            setUserQuota(data.quota);
            setUser(data);
        } else {
            showError(message);
        }
    }

    useEffect(() => {
        let status = localStorage.getItem('status');
        if (status) {
            status = JSON.parse(status);
            if (status.top_up_link) {
                setTopUpLink(status.top_up_link);
            }
        }
        getUserQuota().then();
        loadTopUpPackages().then();
        checkAvailablePaymentMethods().then();

        // 清理轮询
        return () => {
            if (paymentPollingTimer) {
                clearInterval(paymentPollingTimer);
            }
        };
    }, []);

    // 获取状态标签颜色
    const getStatusColor = (status) => {
        switch (status) {
            case '成功': return 'green';
            case '待支付': return 'orange';
            case '已取消': return 'red';
            case '已过期': return 'grey';
            default: return 'default';
        }
    };

    return (
        <div style={{ padding: '24px', background: '#f5f7fa', minHeight: 'calc(100vh - 60px)' }}>
            {/* 页面标题区域 - 参考首页风格 */}
            <div style={{ 
                marginBottom: '24px',
                padding: '24px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '12px',
                color: '#fff'
            }}>
                <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '600' }}>
                    💳 充值中心
                </h2>
                <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
                    为账户充值额度，支持多种支付方式，快速便捷
                </p>
            </div>

            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                {/* 余额卡片 - 优化样式 */}
                <Card style={{ 
                    marginBottom: 24, 
                    textAlign: 'center',
                    borderRadius: '8px',
                    border: '1px solid #e4e7ed',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff'
                }}>
                    <div style={{ padding: '24px' }}>
                        <div style={{ fontSize: '14px', marginBottom: '8px', opacity: 0.9, color: '#fff' }}>当前余额</div>
                        <div style={{ fontSize: '36px', fontWeight: 'bold', lineHeight: 1, color: '#fff' }}>
                            {renderQuota(userQuota)}
                        </div>
                    </div>
                </Card>

                <Tabs
                    type="line"
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    style={{ marginBottom: 24 }}
                >
                            <Tabs.TabPane tab={<span><IconMoneyExchangeStroked style={{ marginRight: 4 }} />在线充值</span>} itemKey="1">
                                {/* 充值套餐选择 - 优化样式 */}
                                <Card style={{ 
                                    marginBottom: 24,
                                    borderRadius: '8px',
                                    border: '1px solid #e4e7ed',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                                }}>
                                    <div style={{ padding: '20px' }}>
                                        <Title level={4} style={{ marginBottom: 20 }}>选择充值套餐</Title>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                            gap: 16
                                        }}>
                                            {topUpPackages.map(pkg => (
                                                <Card
                                                    key={pkg.id}
                                                    onClick={() => setSelectedPackage(pkg)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        border: selectedPackage?.id === pkg.id ? '2px solid #667eea' : '1px solid #e4e7ed',
                                                        background: selectedPackage?.id === pkg.id ? '#fff' : '#fff',
                                                        transition: 'all 0.3s ease',
                                                        position: 'relative',
                                                        borderRadius: '8px',
                                                        boxShadow: selectedPackage?.id === pkg.id ? '0 4px 16px rgba(102, 126, 234, 0.25)' : '0 2px 8px rgba(0, 0, 0, 0.04)',
                                                        transform: selectedPackage?.id === pkg.id ? 'translateY(-2px)' : 'translateY(0)'
                                                    }}
                                                    shadows={selectedPackage?.id === pkg.id ? 'always' : 'hover'}
                                                >
                                                    {pkg.discount && (
                                                        <Tag
                                                            color="orange"
                                                            style={{
                                                                position: 'absolute',
                                                                top: 8,
                                                                right: 8,
                                                                borderRadius: '12px',
                                                                padding: '4px 10px',
                                                                fontSize: '12px',
                                                                fontWeight: '600',
                                                                zIndex: 1
                                                            }}
                                                        >
                                                            {pkg.discount}
                                                        </Tag>
                                                    )}
                                                    <div style={{ textAlign: 'center', padding: '20px 12px' }}>
                                                        <Title level={5} style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>{pkg.name}</Title>
                                                        <div style={{ 
                                                            fontSize: 28, 
                                                            fontWeight: 'bold', 
                                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                            WebkitBackgroundClip: 'text',
                                                            WebkitTextFillColor: 'transparent',
                                                            marginBottom: 12,
                                                            lineHeight: 1.2
                                                        }}>
                                                            ¥{pkg.price}
                                                        </div>
                                                        <div style={{ color: '#909399', fontSize: 13, lineHeight: 1.5 }}>
                                                            额度: {renderQuota(pkg.quota)}
                                                        </div>
                                                    </div>
                                                </Card>
                                            ))}
                                        </div>
                                    </div>
                                </Card>

                                {/* 支付方式选择 - 优化样式 */}
                                <Card style={{ 
                                    marginBottom: 24,
                                    borderRadius: '8px',
                                    border: '1px solid #e4e7ed',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                                }}>
                                    <div style={{ padding: '20px' }}>
                                        <Title level={4} style={{ marginBottom: 20 }}>选择支付方式</Title>
                                        {!availablePaymentMethods.find(m => m.id === 'alipay' && m.enabled) && (
                                            <div style={{ 
                                                padding: 12, 
                                                marginBottom: 16, 
                                                background: '#fff7e6', 
                                                border: '1px solid #ffd591',
                                                borderRadius: 8,
                                                color: '#fa8c16',
                                                fontSize: 13
                                            }}>
                                                ⚠️ 支付宝功能尚未配置，建议使用微信支付。如需使用支付宝，请联系管理员配置支付宝参数。
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                            {availablePaymentMethods.filter(m => m.enabled).map(method => (
                                                <Card
                                                    key={method.id}
                                                    onClick={() => setPaymentMethod(method.id)}
                                                    style={{
                                                        flex: 1,
                                                        minWidth: 120,
                                                        cursor: 'pointer',
                                                        textAlign: 'center',
                                                        border: paymentMethod === method.id ? '2px solid ' + method.color : '1px solid #e4e7ed',
                                                        background: paymentMethod === method.id ? method.color + '10' : '#fff',
                                                        transition: 'all 0.3s ease',
                                                        borderRadius: '8px',
                                                        boxShadow: paymentMethod === method.id ? '0 4px 12px rgba(0, 0, 0, 0.1)' : '0 2px 8px rgba(0, 0, 0, 0.04)'
                                                    }}
                                                    shadows={paymentMethod === method.id ? 'always' : 'hover'}
                                                >
                                                    <div style={{ color: method.color, marginBottom: 8 }}>
                                                        {method.icon}
                                                    </div>
                                                    <div style={{ fontSize: 14, fontWeight: 'bold' }}>{method.name}</div>
                                                </Card>
                                            ))}
                                        </div>
                                    </div>
                                </Card>

                                {/* 确认充值按钮 - 优化样式 */}
                                <Button
                                    type="primary"
                                    theme="solid"
                                    size="large"
                                    block
                                    onClick={handlePayment}
                                    disabled={!selectedPackage || isLoading}
                                    loading={isLoading}
                                    style={{ 
                                        height: 48, 
                                        fontSize: 16, 
                                        fontWeight: '600',
                                        borderRadius: '8px',
                                        background: (!selectedPackage || isLoading) ? '#c0c4cc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        border: 'none',
                                        color: '#fff',
                                        boxShadow: (!selectedPackage || isLoading) ? 'none' : '0 4px 12px rgba(102, 126, 234, 0.35)'
                                    }}
                                >
                                    立即充值
                                </Button>
                            </Tabs.TabPane>

                            <Tabs.TabPane tab={<span><IconGift style={{ marginRight: 4 }} />兑换码</span>} itemKey="2">
                                <Card style={{
                                    borderRadius: '8px',
                                    border: '1px solid #e4e7ed',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                                }}>
                                    <div style={{ padding: '20px' }}>
                                        <Title level={4} style={{ marginBottom: 20 }}>兑换码充值</Title>
                                        <Form>
                                            <Form.Input
                                                field={'redemptionCode'}
                                                label={'兑换码'}
                                                placeholder='请输入兑换码'
                                                value={redemptionCode}
                                                onChange={(value) => setRedemptionCode(value)}
                                                style={{ marginBottom: 16 }}
                                            />
                                            <Space>
                                                {topUpLink && (
                                                    <Button type="tertiary" theme={'solid'} onClick={openTopUpLink} style={{ borderRadius: '8px' }}>
                                                        获取兑换码
                                                    </Button>
                                                )}
                                                <Button type="warning" theme={'solid'} onClick={topUp} disabled={isSubmitting} style={{ 
                                                    borderRadius: '8px',
                                                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                                    border: 'none',
                                                    color: '#fff'
                                                }}>
                                                    {isSubmitting ? '兑换中...' : '立即兑换'}
                                                </Button>
                                            </Space>
                                        </Form>
                                    </div>
                                </Card>
                            </Tabs.TabPane>

                            <Tabs.TabPane tab={<span><IconHistory style={{ marginRight: 4 }} />充值记录</span>} itemKey="3">
                                <Card style={{
                                    borderRadius: '8px',
                                    border: '1px solid #e4e7ed',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                                }}>
                                    <div style={{ padding: '20px' }}>
                                        <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Title level={4} style={{ margin: 0 }}>充值记录</Title>
                                            <Button
                                                type="primary"
                                                theme="solid"
                                                onClick={() => {
                                                    if (!showHistory) {
                                                        setShowHistory(true);
                                                        loadPaymentHistory();
                                                    } else {
                                                        loadPaymentHistory();
                                                    }
                                                }}
                                                style={{ borderRadius: '8px' }}
                                            >
                                                刷新记录
                                            </Button>
                                        </div>

                                        {paymentHistory.length > 0 ? (
                                            <div style={{ overflowX: 'auto' }}>
                                                <table style={{
                                                    width: '100%',
                                                    borderCollapse: 'collapse',
                                                    fontSize: 14
                                                }}>
                                                    <thead>
                                                        <tr style={{ borderBottom: '2px solid #e4e7ed' }}>
                                                            <th style={{ padding: 12, textAlign: 'left' }}>订单号</th>
                                                            <th style={{ padding: 12, textAlign: 'left' }}>充值额度</th>
                                                            <th style={{ padding: 12, textAlign: 'left' }}>支付金额</th>
                                                            <th style={{ padding: 12, textAlign: 'left' }}>支付方式</th>
                                                            <th style={{ padding: 12, textAlign: 'left' }}>状态</th>
                                                            <th style={{ padding: 12, textAlign: 'left' }}>时间</th>
                                                            <th style={{ padding: 12, textAlign: 'left' }}>操作</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {paymentHistory.map(record => (
                                                            <tr key={record.id} style={{ borderBottom: '1px solid #e4e7ed' }}>
                                                                <td style={{ padding: 12, fontFamily: 'monospace', fontSize: 12 }}>{record.orderNo}</td>
                                                                <td style={{ padding: 12 }}>{renderQuota(record.quota)}</td>
                                                                <td style={{ padding: 12, fontWeight: 'bold', color: '#667eea' }}>¥{record.amount}</td>
                                                                <td style={{ padding: 12 }}>{record.method}</td>
                                                                <td style={{ padding: 12 }}>
                                                                    <Tag color={getStatusColor(record.status)}>{record.status}</Tag>
                                                                </td>
                                                                <td style={{ padding: 12, color: '#909399', fontSize: 13 }}>{record.time}</td>
                                                                <td style={{ padding: 12 }}>
                                                                    <Button
                                                                        type="tertiary"
                                                                        size="small"
                                                                        onClick={() => copyOrderNo(record.orderNo)}
                                                                        style={{ borderRadius: '6px' }}
                                                                    >
                                                                        复制
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div style={{ textAlign: 'center', padding: '40px', color: '#909399' }}>
                                                <IconHistory style={{ fontSize: 48, marginBottom: 16 }} />
                                                <p>暂无充值记录</p>
                                            </div>
                                        )}
                                    </div>
                                </Card>
                            </Tabs.TabPane>
                        </Tabs>
                    </div>

                    {/* 支付二维码弹窗 - 优化样式 */}
                    <Modal
                        title="扫码支付"
                        visible={showQRCode}
                        onCancel={() => setShowQRCode(false)}
                        footer={null}
                        centered
                        width={400}
                    >
                        {selectedPackage && (
                            <div style={{ textAlign: 'center' }}>
                                {paymentMethod === 'alipay' && (
                                    <div style={{
                                        background: '#e6f7ff',
                                        border: '1px solid #91d5ff',
                                        borderRadius: '4px',
                                        padding: '12px',
                                        marginBottom: '16px',
                                        textAlign: 'left'
                                    }}>
                                        <div style={{ color: '#0050b3', fontSize: '14px' }}>
                                            <strong>💡 支付宝支付说明</strong><br/>
                                            <span>
                                                请使用支付宝APP扫描下方二维码<br/>
                                                扫码后将自动跳转到支付页面完成支付
                                            </span>
                                        </div>
                                    </div>
                                )}
                                <p style={{ color: '#606266', marginBottom: 20 }}>
                                    使用{paymentMethod === 'wechat' ? '微信' : paymentMethod === 'alipay' ? '支付宝' : '银行卡'}扫描二维码完成支付
                                </p>

                                <div style={{
                                    background: '#f5f7fa',
                                    padding: 24,
                                    borderRadius: 8,
                                    marginBottom: 20
                                }}>
                                    {qrCodeUrl ? (
                                        <img
                                            src={qrCodeUrl}
                                            alt='支付二维码'
                                            style={{ width: 200, height: 200 }}
                                        />
                                    ) : (
                                        <Spin size="large" />
                                    )}
                                </div>

                                <Card style={{ 
                                    marginBottom: 20, 
                                    textAlign: 'left',
                                    borderRadius: '8px',
                                    border: '1px solid #e4e7ed'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                        <span style={{ color: '#606266' }}>充值套餐:</span>
                                        <span style={{ fontWeight: 'bold' }}>{selectedPackage.name}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                        <span style={{ color: '#606266' }}>充值额度:</span>
                                        <span style={{ fontWeight: 'bold', color: '#667eea' }}>{renderQuota(selectedPackage.quota)}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, borderTop: '1px solid #e4e7ed', paddingTop: 12 }}>
                                        <span style={{ fontWeight: 'bold' }}>支付金额:</span>
                                        <span style={{ color: '#f5576c', fontWeight: 'bold' }}>¥{selectedPackage.price}</span>
                                    </div>
                                </Card>

                                <Space>
                                    <Button onClick={() => setShowQRCode(false)} style={{ borderRadius: '8px' }}>
                                        取消支付
                                    </Button>
                                    <Button type="primary" theme="solid" onClick={checkPaymentStatus} style={{ 
                                        borderRadius: '8px',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        border: 'none',
                                        color: '#fff'
                                    }}>
                                        已完成支付
                                    </Button>
                                </Space>
                            </div>
                        )}
                    </Modal>
        </div>
    );
};

export default TopUp;